import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Typography } from '@heroui/react';
import { countParam } from '@/pages/catalogImport/notices';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type ExcludedRowsBarProps = {
    /** The rows the user excluded: the Excluded tab's count. */
    excludedCount: number;
    /** The import is running: nothing is taken back until it answers. */
    isDisabled: boolean;
    /** Include all back. */
    onIncludeAll: () => void;
};

/**
 * Over the Excluded tab's table (bos-app#220, US-09): what an excluded row
 * means, and **Include all back** (secondary, small), which takes back every
 * excluded row as one bulk action. Each row keeps its own Include in the
 * table. Nothing when no row is excluded: the empty table says so.
 */
export const ExcludedRowsBar: React.FC<ExcludedRowsBarProps> = ({
    excludedCount,
    isDisabled,
    onIncludeAll,
}) => {
    const { t } = useTranslation('pages');

    if (excludedCount === 0) {
        return null;
    }

    return (
        <div
            className="flex flex-col gap-3 pb-3 sm:flex-row sm:items-center"
            {...getNodeTestIdProp('catalog-import-products-excluded-bar')}
        >
            <Typography className="min-w-0 flex-1" color="muted" type="body-sm">
                {t('catalogImport.products.excluded.intro')}
            </Typography>
            <Button
                className="w-full shrink-0 sm:w-auto"
                isDisabled={isDisabled}
                onPress={onIncludeAll}
                size="sm"
                variant="secondary"
                {...getNodeTestIdProp('catalog-import-products-include-all')}
            >
                {t('catalogImport.products.excluded.includeAll', {
                    count: excludedCount,
                    rows: countParam(excludedCount),
                })}
            </Button>
        </div>
    );
};
